import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Engine, Gauge, Lightning, Drop, Calendar, MapPin } from "@phosphor-icons/react";
import { fetchBike } from "@/lib/api";

export default function BikeDetail() {
  const { id } = useParams();
  const [bike, setBike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchBike(id)
      .then((b) => {
        setBike(b);
        setActiveImg(b.image_url);
      })
      .catch(() => setBike(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div data-testid="bike-detail-loading" className="pt-28 md:pt-36 max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="aspect-video bg-[#141414] animate-pulse" />
      </div>
    );
  }

  if (!bike) {
    return (
      <div data-testid="bike-detail-not-found" className="pt-28 md:pt-36 max-w-7xl mx-auto px-6 md:px-12 pb-24 text-center">
        <h1 className="font-display font-black uppercase text-4xl">Η μηχανή δεν βρέθηκε</h1>
        <Link to="/catalog" className="btn-primary text-xs mt-6 inline-flex">Πίσω στον κατάλογο</Link>
      </div>
    );
  }

  const gallery = [bike.image_url, ...(bike.gallery || [])].filter((v, i, a) => a.indexOf(v) === i);
  const specs = [
    { icon: Engine, label: "Κινητήρας", value: `${bike.engine_cc}cc` },
    { icon: Lightning, label: "Ισχύς", value: `${bike.power_hp} hp` },
    { icon: Gauge, label: "Χιλιόμετρα", value: `${bike.mileage_km.toLocaleString()} χλμ` },
    { icon: Drop, label: "Βάρος", value: `${bike.weight_kg} kg` },
    { icon: Calendar, label: "Έτος", value: `${bike.year}` },
    { icon: MapPin, label: "Χρώμα", value: bike.color },
  ];

  return (
    <div data-testid="bike-detail-page" className="pt-36 md:pt-48 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link to="/catalog" data-testid="back-to-catalog-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-[#E50000] mb-8">
          <ArrowLeft size={14} weight="bold" /> Πίσω στον κατάλογο
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="bg-[#141414] border border-white/10 aspect-[4/3] overflow-hidden">
              <img src={activeImg} alt={`${bike.brand} ${bike.model}`} className="w-full h-full object-cover" />
            </div>
            {gallery.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(g)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`aspect-square overflow-hidden border ${activeImg === g ? "border-[#E50000]" : "border-white/10 hover:border-white/40"}`}
                  >
                    <img src={g} alt="thumb" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="flex gap-2 mb-5">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] bg-[#E50000] text-white px-2 py-1">{bike.condition === "New" ? "Καινούργιο" : "Μεταχ."}</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] bg-transparent border border-white/15 text-white px-2 py-1">{bike.category}</span>
            </div>
            <p className="text-overline mb-3">{bike.brand} · {bike.year}</p>
            <h1 data-testid="bike-detail-name" className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl leading-[0.95]">{bike.model}</h1>
            <p className="mt-6 text-white/65 leading-relaxed">{bike.description}</p>

            <div className="mt-8 flex items-end gap-6 border-y border-white/10 py-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40 font-mono">Τιμή</p>
                <p data-testid="bike-detail-price" className="font-display font-black text-4xl text-white">€{bike.price.toLocaleString()}</p>
              </div>
              <p className="text-xs font-mono text-white/40 ml-auto">ΦΠΑ συμπ. · διαθέσιμη χρηματοδότηση</p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {specs.map((s, i) => (
                <div key={i} className="border border-white/10 p-4 flex items-start gap-3">
                  <s.icon size={20} weight="duotone" className="text-[#E50000] mt-0.5" />
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 font-mono">{s.label}</p>
                    <p className="font-display font-bold text-white">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={`/test-ride/${bike.id}`} data-testid="detail-book-test-ride-btn" className="btn-primary text-xs">Κλείσε Δοκιμή</Link>
              <Link to="/service" data-testid="detail-service-btn" className="btn-outline text-xs">Μίλησε με το Εργαστήριο</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
